<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import { ref, onMounted, watch } from 'vue'

const { getStats } = useHome()
const { data: stats } = await useAsyncData('home-stats', () => getStats())

// Reactive array for animated values
const animatedValues = ref<number[]>([])
const sectionRef = ref<HTMLElement | null>(null)
const hasAnimated = ref(false)

// Initialize values
if (stats.value) {
  animatedValues.value = stats.value.map(() => 0)
}

// Watch for data changes if hydration updates it
watch(stats, (newStats) => {
  if (newStats) {
    animatedValues.value = newStats.map(() => 0)
  }
})

const easeOutQuart = (x: number): number => {
  return 1 - Math.pow(1 - x, 4)
}

const animateStats = () => {
  if (hasAnimated.value || !stats.value) return
  hasAnimated.value = true

  stats.value.forEach((stat, index) => {
    const start = 0
    const end = stat.value
    const duration = 2000 // 2 seconds
    const startTime = performance.now()

    const update = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1) // 0 to 1
      const easedProgress = easeOutQuart(progress)

      const current = Math.round(start + (end - start) * easedProgress)
      animatedValues.value[index] = current

      if (progress < 1) {
        requestAnimationFrame(update)
      } else {
        animatedValues.value[index] = end // Ensure final value is exact
      }
    }

    requestAnimationFrame(update)
  })
}

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        animateStats()
        // Optional: stop observing once started or keep it to trigger only once
        if (sectionRef.value) observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.2 })

  if (sectionRef.value) {
    observer.observe(sectionRef.value)
  }
})
</script>

<template>
  <section v-if="stats && stats.length > 0" ref="sectionRef" class="py-section-py bg-background border-y border-oxo-border relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px">
      <dl v-if="stats" class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
        <div 
          v-for="(stat, index) in stats" 
          :key="index" 
          class="flex flex-col items-center text-center group"
        >
          <dt class="sr-only">{{ stat.label }} Icon</dt>
          <dd class="mb-4 text-oxo-red text-3xl md:text-4xl transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
            <i :style="faIconStyle('fas fa-' + stat.icon.replace('fa-', ''))" :class="'fas fa-' + stat.icon.replace('fa-', '')" aria-hidden="true"></i>
          </dd>
          
          <dd class="font-display font-black text-4xl md:text-5xl text-foreground mb-2">
            <!-- Use animated value -->
            <span class="counter">{{ animatedValues[index] }}</span>{{ stat.suffix }}
          </dd>
          
          <dt class="text-foreground-dim font-medium uppercase tracking-wider text-sm">
            {{ stat.label }}
          </dt>
        </div>
      </dl>
    </div>
  </section>
</template>
